import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Search, User } from 'lucide-react';
import {
  useEligibleUsers,
  useAdminBookUser,
  EligibleUser,
} from '@/api/classes';

export interface AddUserToClassDialogProps {
  classId: string;
  timeSlotId: string;
  date: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AddUserToClassDialog({
  classId,
  timeSlotId,
  date,
  open,
  onOpenChange,
}: AddUserToClassDialogProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState<EligibleUser | null>(null);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  const {
    data: eligibleUsers,
    isLoading: isLoadingUsers,
  } = useEligibleUsers(classId);

  const { mutateAsync: adminBook, isPending } = useAdminBookUser();

  const handleUserSelect = (user: EligibleUser) => {
    setSelectedUser(user);
    setIsUserMenuOpen(false);
  };

  const handleSubmit = async () => {
    if (!selectedUser) return;

    try {
      await adminBook({
        classId,
        timeSlotId,
        data: {
          userId: selectedUser._id,
          fullName: `${selectedUser.name} ${selectedUser.surname}`.trim(),
          date,
        },
      });
      toast({
        title: t('common.success', 'Success'),
        description: t('classBookings.userAddedToClass', '{{name}} added to class', { name: `${selectedUser.name} ${selectedUser.surname}` }),
      });
      setSelectedUser(null);
      setSearchQuery('');
      onOpenChange(false);
    } catch (error: any) {
      toast({
        title: t('common.error', 'Error'),
        description:
          error?.response?.data?.message || t('classBookings.failedToAddUser', 'Failed to add user to class'),
        variant: 'destructive',
      });
    }
  };

  const filteredUsers = (eligibleUsers ?? []).filter(
    (user) =>
      user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.surname.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('classBookings.addUserToClass', 'Add User to Class')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>{t('classBookings.selectUser', 'Select User')}</Label>
            <Popover open={isUserMenuOpen} onOpenChange={setIsUserMenuOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={isUserMenuOpen}
                  className="w-full justify-between"
                  type="button"
                >
                  {selectedUser ? (
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 shrink-0" />
                      <span>
                        {selectedUser.name} {selectedUser.surname}
                      </span>
                      {selectedUser.remainingEntries !== null && (
                        <Badge variant="secondary" className="text-xs ml-auto">
                          {t('classBookings.entriesLeft', '{{count}} left', { count: selectedUser.remainingEntries })}
                        </Badge>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center text-muted-foreground">
                      <Search className="mr-2 h-4 w-4" />
                      <span>{t('classBookings.searchEligibleUsers', 'Search eligible users...')}</span>
                    </div>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                <Command shouldFilter={false}>
                  <CommandInput
                    placeholder={t('classBookings.searchByNameOrEmail', 'Search by name or email...')}
                    value={searchQuery}
                    onValueChange={setSearchQuery}
                  />
                  <CommandList>
                    <CommandEmpty>
                      {isLoadingUsers
                        ? t('common.loading', 'Loading...')
                        : t('classBookings.noEligibleUsers', 'No eligible users found')}
                    </CommandEmpty>
                    {isLoadingUsers ? (
                      <div className="flex items-center justify-center p-4">
                        <Loader2 className="h-6 w-6 animate-spin text-primary" />
                      </div>
                    ) : (
                      <ScrollArea className="h-[220px]">
                        <CommandGroup>
                          {filteredUsers.map((user) => (
                            <CommandItem
                              key={user._id}
                              value={user._id}
                              onSelect={() => handleUserSelect(user)}
                              className="flex items-center justify-between"
                            >
                              <div className="flex flex-col min-w-0">
                                <span className="font-medium truncate">
                                  {user.name} {user.surname}
                                </span>
                                <span className="text-xs text-muted-foreground truncate">
                                  {user.email}
                                </span>
                              </div>
                              <Badge
                                variant={
                                  user.remainingEntries === null
                                    ? 'default'
                                    : user.remainingEntries > 3
                                      ? 'secondary'
                                      : 'destructive'
                                }
                                className="text-xs shrink-0 ml-2"
                              >
                                {user.remainingEntries === null
                                  ? t('classBookings.unlimited', 'Unlimited')
                                  : t('classBookings.entriesLeft', '{{count}} left', { count: user.remainingEntries })}
                              </Badge>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </ScrollArea>
                    )}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              type="button"
              disabled={!selectedUser || isPending}
              onClick={handleSubmit}
            >
              {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('classBookings.addToClass', 'Add to Class')}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
